import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';

/** Onboarding guiado do painel (docs/requisito-onboarding-painel-v1.md). Espelha /onboarding/* do servidor. */
export type PassoOnboardingId = 'cardapio' | 'loja' | 'horarios' | 'pagamento' | 'receber' | 'teste';
export type DispositivoOnboarding = 'CELULAR' | 'COMPUTADOR';

export const PASSOS_ONBOARDING: PassoOnboardingId[] = ['cardapio', 'loja', 'horarios', 'pagamento', 'receber', 'teste'];

export interface PassoOnboarding { id: PassoOnboardingId; nome: string; feito: boolean; iniciado: boolean; detalhe: string; }

export interface PedidoTesteOnboarding {
  id: number; guid: string; codigo: string; status: number; aceito: boolean; valor: number; taxaEntrega: number;
  formaDeEntregaId: number; contatoNome: string; horario: string;
  itens: Array<{id: number; qtde: number; descricao: string; total: number}>;
}

export interface LojaAtualOnboarding {
  delivery: boolean; retirada: boolean; taxa: number | null; tempoMinimo: number | null; tempoMaximo: number | null;
  tempoRetiradaMinimo: number | null; tempoRetiradaMaximo: number | null; pedidoMinimo: number | null; endereco: string; cep: string;
}

export interface DiaDaGrade { dia: number; aberto: boolean; abre: string; fecha: string; }

export interface MenuDoOnboarding {
  ativo: boolean; fase: number; menuCompleto: boolean; receberFeito: boolean; experiente: boolean; reveladas: string[]; novas: string[];
}

export interface EstadoOnboardingPainel {
  ativo: boolean;
  fase?: number;
  menuCompleto?: boolean;
  dispositivo?: DispositivoOnboarding | null;
  passos?: PassoOnboarding[];
  feitos?: number;
  total?: number;
  proximo?: PassoOnboardingId | null;
  pedidoTeste?: PedidoTesteOnboarding | null;
  equipamentos?: {impressora: string | null; extensaoConectada: boolean; notificacao: boolean};
  bonus?: {valor: number; prazo: string | null; disponivel: boolean; garantido: boolean; aplicado: boolean};
  evolucao?: {pct: number; fase: number; nomeFase: string; proximas: string[]};
  menu?: MenuDoOnboarding;
  loja?: LojaAtualOnboarding;
  grade?: DiaDaGrade[];
  telefoneGestor?: string | null;
  linkDivulgado?: boolean;
  pedidosReais?: number;
  mia?: FichaDaMia | null;
}

/** O que a Mia anotou na conversa comercial. */
export interface FichaDaMia { restaurante: string | null; segmento: string | null; operacao: string | null; canais: string | null;
  ferramentaAtual: string | null; instagram: string | null; cidade: string | null; }

export interface ProdutoOnboarding { id: number; nome: string; descricao: string; preco: number | null; categoria: string; tipo: string; tamanhos: number; }

export interface EnderecoPorCep { cep: string; rua: string; bairro: string; cidade: string; estado: string; }

export type EventoOnboarding = 'PASSO_ABERTO' | 'PASSO_FEITO' | 'VIDEO_ABERTO' | 'AJUDA_ABERTA' | 'AJUDA_WHATSAPP' | 'IMPRESSORA_FALHOU'
  | 'IMPORTACAO_FALHOU' | 'EXTENSAO_ABERTA' | 'MENU_DESCOBRIR' | 'LINK_TESTE';

@Injectable({providedIn: 'root'})
export class OnboardingPainelService {
  constructor(private http: HttpClient) {}

  /** Mensagem de erro que o servidor mandou (400) ou uma genérica. */
  static mensagem(erro: any, padrao = 'Não foi possível salvar. Tente de novo.'): string {
    return erro?.error?.erro || padrao;
  }

  estado(): Promise<EstadoOnboardingPainel> {
    return this.http.get<EstadoOnboardingPainel>('/onboarding/estado').toPromise();
  }

  confirme(passo: 'pagamento' | 'receber' | 'notificacao' | 'extensao' | 'impressora' | 'link-divulgado',
           dados: {dispositivo?: DispositivoOnboarding; impressora?: string | null} = {}): Promise<EstadoOnboardingPainel> {
    return this.http.put<EstadoOnboardingPainel>(`/onboarding/passos/${passo}`, dados).toPromise();
  }

  busqueCep(cep: string): Promise<EnderecoPorCep> {
    return this.http.get<EnderecoPorCep>(`/onboarding/cep/${encodeURIComponent(cep)}`).toPromise();
  }

  salveLoja(dados: any): Promise<EstadoOnboardingPainel> {
    return this.http.put<EstadoOnboardingPainel>('/onboarding/loja', dados).toPromise();
  }

  salveHorarios(grade: DiaDaGrade[]): Promise<EstadoOnboardingPainel> {
    return this.http.put<EstadoOnboardingPainel>('/onboarding/horarios', {grade}).toPromise();
  }

  produtos(): Promise<ProdutoOnboarding[]> {
    return this.http.get<ProdutoOnboarding[]>('/onboarding/produtos').toPromise();
  }

  salvePreco(produtoId: number, preco: string | number): Promise<any> {
    return this.http.put(`/onboarding/produtos/${produtoId}/preco`, {preco}).toPromise();
  }

  linkPedidoTeste(): Promise<{link: string}> {
    return this.http.post<{link: string}>('/onboarding/pedido-teste/link', {}).toPromise();
  }

  conclua(): Promise<EstadoOnboardingPainel> {
    return this.http.post<EstadoOnboardingPainel>('/onboarding/concluir', {}).toPromise();
  }

  prepareExtensao(): Promise<{whatsapp: string}> {
    return this.http.post<{whatsapp: string}>('/onboarding/extensao/preparar', {}).toPromise();
  }

  revele(funcaoId: string, origem: 'DESCOBRIR' | 'BUSCA'): Promise<MenuDoOnboarding> {
    return this.http.post<MenuDoOnboarding>('/onboarding/funcoes/revelar', {funcaoId, origem}).toPromise();
  }

  marqueVista(funcaoId: string): Promise<any> {
    return this.http.put('/onboarding/funcoes/vista', {funcaoId}).toPromise();
  }

  definaMenuCompleto(completo: boolean): Promise<MenuDoOnboarding> {
    return this.http.put<MenuDoOnboarding>('/onboarding/menu-completo', {completo}).toPromise();
  }

  /** Telemetria de uso: nunca atrapalha a tela. */
  registre(tipo: EventoOnboarding, detalhe?: string): void {
    this.http.post('/onboarding/eventos', {tipo, detalhe: detalhe || null}).toPromise().catch(() => {});
  }

  /** Status do pedido pelas rotas do próprio painel: é exatamente o que o dono vai fazer no dia a dia. */
  aceitePedido(guid: string): Promise<any> {
    return this.http.put(`/pedidos/${guid}/aceitar`, {}).toPromise().then(OnboardingPainelService.resposta);
  }

  mudeStatusPedido(guid: string, status: number): Promise<any> {
    return this.http.put(`/pedidos/${guid}/status`, {status, notificar: true}).toPromise().then(OnboardingPainelService.resposta);
  }

  private static resposta(r: any): any {
    if (r && r.sucesso === false) throw {error: {erro: r.erro || 'Não foi possível atualizar o pedido.'}};
    return r?.data ?? r;
  }
}
